import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import type {
  LandingIntroCenteredTextContent,
  LandingIntroFeatureContent,
  LandingIntroImageBlockContent,
  LandingIntroSection,
} from '../../types/landingIntro';
import {
  clampLandingIntroHeightRatio,
  introImageSrc,
  parseIntroBlockContent,
} from '../../types/landingIntro';
import {
  landingIntroFrameProps,
  landingIntroFrameStyle,
  resolveLandingIntroPageBackground,
} from '../../utils/landingIntroPageBackground';

const ProgramSlideCanvas = lazy(() => import('../program/ProgramSlideCanvas'));

/** 편집·디자인 기준 폭. 공개 페이지는 이 크기로 그린 뒤 프레임 너비에 맞춰 균등 scale */
const LANDING_INTRO_SLIDE_DESIGN_WIDTH_PX = 1100;

function FeatureBlock({
  section,
  index,
}: {
  section: LandingIntroSection;
  index: number;
}) {
  const content = parseIntroBlockContent('feature_like', section.content_json) as LandingIntroFeatureContent;
  const imageUrl = introImageSrc(content.image);
  // 홈 FEATURE와 동일: 짝수 인덱스가 reverse (텍스트 왼쪽·이미지 오른쪽)
  const reverse = typeof content.reverse === 'boolean' ? content.reverse : index % 2 === 0;
  const ctaLabel = (content.ctaLabel || '').trim();

  return (
    <section
      className={`landing-feature landing-intro-block-feature${
        reverse ? ' landing-feature--reverse' : ''
      }`}
      data-landing-reveal
    >
      <div className="landing-feature-content">
        {imageUrl ? (
          <div className="landing-feature-image-wrap">
            <img
              src={imageUrl}
              alt={content.title || '소개 이미지'}
              loading="lazy"
              className="landing-feature-image"
            />
          </div>
        ) : (
          <div className="landing-feature-image-placeholder">
            <span className="landing-feature-image-placeholder-text">이미지 준비중</span>
          </div>
        )}
        <div className="landing-feature-text">
          {content.title ? <h2 className="landing-feature-title">{content.title}</h2> : null}
          {content.subtitle ? (
            <p className="landing-feature-subtitle">{content.subtitle}</p>
          ) : null}
          {content.body ? <p className="landing-feature-body">{content.body}</p> : null}
          {ctaLabel ? (
            content.ctaLink ? (
              <a className="landing-feature-cta-btn" href={content.ctaLink}>
                {ctaLabel}
              </a>
            ) : (
              <span className="landing-feature-cta-btn">{ctaLabel}</span>
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}

function CenteredTextBlock({ section }: { section: LandingIntroSection }) {
  const content = parseIntroBlockContent(
    'centered_text',
    section.content_json,
  ) as LandingIntroCenteredTextContent;
  return (
    <section className="landing-intro-centered-text" data-landing-reveal>
      {content.title ? <h2 className="landing-intro-centered-title">{content.title}</h2> : null}
      {content.subtitle ? (
        <p className="landing-intro-centered-subtitle">{content.subtitle}</p>
      ) : null}
      {content.body ? <p className="landing-intro-centered-body">{content.body}</p> : null}
    </section>
  );
}

function ImageCarouselBlock({ content }: { content: LandingIntroImageBlockContent }) {
  const slides = content.images
    .map((img, i) => ({ id: `${i}-${img.path ?? img.url ?? ''}`, src: introImageSrc(img), alt: img.alt || '' }))
    .filter((s) => s.src);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div className="landing-intro-image-placeholder" aria-label="이미지 준비중">
        이미지 준비중
      </div>
    );
  }

  return (
    <div className="landing-intro-image-media" role="region" aria-roledescription="carousel">
      <div
        className="landing-intro-image-carousel"
        onClick={() => {
          if (slides.length > 1) setIndex((prev) => (prev + 1) % slides.length);
        }}
        role={slides.length > 1 ? 'button' : undefined}
        tabIndex={slides.length > 1 ? 0 : undefined}
        aria-label={slides.length > 1 ? '다음 이미지' : undefined}
        onKeyDown={
          slides.length > 1
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIndex((prev) => (prev + 1) % slides.length);
                }
              }
            : undefined
        }
      >
        <div className="landing-intro-image-carousel-track">
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              className={`landing-intro-image-carousel-slide${i === index ? ' is-active' : ''}`}
              aria-hidden={i !== index}
            >
              <img src={slide.src!} alt={slide.alt || `이미지 ${i + 1}`} loading={i === 0 ? 'eager' : 'lazy'} />
            </div>
          ))}
        </div>
      </div>
      {slides.length > 1 ? (
        <div className="landing-intro-image-carousel-dots" role="tablist">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`landing-intro-image-carousel-dot${i === index ? ' is-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setIndex(i);
              }}
              aria-label={`${i + 1}번째 이미지`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ImageGridBlock({ content }: { content: LandingIntroImageBlockContent }) {
  const cols = content.columns === 3 ? 3 : 2;
  const images = content.images
    .map((img) => ({ src: introImageSrc(img), alt: img.alt || '' }))
    .filter((x) => x.src);

  if (images.length === 0) {
    return (
      <div className="landing-intro-image-placeholder" aria-label="이미지 준비중">
        이미지 준비중
      </div>
    );
  }

  return (
    <div
      className="landing-intro-image-grid"
      style={{ ['--landing-intro-grid-cols' as string]: String(cols) }}
    >
      {images.map((img, i) => (
        <figure key={`${img.src}-${i}`} className="landing-intro-image-grid-item">
          <img src={img.src!} alt={img.alt || `이미지 ${i + 1}`} loading="lazy" />
        </figure>
      ))}
    </div>
  );
}

function ImageBlock({ section }: { section: LandingIntroSection }) {
  const content = parseIntroBlockContent('image', section.content_json) as LandingIntroImageBlockContent;
  return (
    <section className="landing-intro-image-block" data-landing-reveal>
      {content.display === 'grid' ? (
        <ImageGridBlock content={content} />
      ) : (
        <ImageCarouselBlock content={content} />
      )}
    </section>
  );
}

function SlideBlock({ section, index }: { section: LandingIntroSection; index: number }) {
  const pageBg = resolveLandingIntroPageBackground(section.layout_json);
  const heightRatio = clampLandingIntroHeightRatio(section.height_ratio);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const sync = () => {
      const w = el.getBoundingClientRect().width;
      const next = w > 0 ? w / LANDING_INTRO_SLIDE_DESIGN_WIDTH_PX : 1;
      setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };
    sync();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const designHeightPx = LANDING_INTRO_SLIDE_DESIGN_WIDTH_PX * heightRatio;

  return (
    <div
      className="cp-program-slide-root landing-intro-slide-root landing-intro-block-slide"
      data-slide-surface="light"
      data-landing-reveal
    >
      <div
        ref={frameRef}
        className="landing-intro-section-frame"
        role="listitem"
        aria-label={`슬라이드 ${index + 1}`}
        style={landingIntroFrameStyle(heightRatio, pageBg)}
        {...landingIntroFrameProps(pageBg)}
      >
        <div
          className="landing-intro-section-frame-design"
          style={{
            width: LANDING_INTRO_SLIDE_DESIGN_WIDTH_PX,
            height: designHeightPx,
            transform: `scale(${scale})`,
          }}
        >
          <Suspense
            fallback={
              <div className="landing-intro-canvas-fallback" role="status">
                슬라이드 불러오는 중…
              </div>
            }
          >
            <ProgramSlideCanvas
              layout={section.layout_json}
              onLayoutChange={() => {}}
              readOnly
              viewMode
              canvasAspectRatio="presentation"
              defaultTextColorHex={section.layout_json.defaultTextColorHex}
              canvasBackgroundColorHex={pageBg}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export function LandingIntroBlockStack({ sections }: { sections: LandingIntroSection[] }) {
  let featureIndex = 0;
  let slideIndex = 0;
  return (
    <div className="landing-features landing-intro-block-stack" role="list">
      {sections.map((section) => {
        if (section.section_kind === 'slide') {
          const idx = slideIndex;
          slideIndex += 1;
          return <SlideBlock key={section.id} section={section} index={idx} />;
        }
        if (section.section_kind === 'feature_like') {
          const idx = featureIndex;
          featureIndex += 1;
          return <FeatureBlock key={section.id} section={section} index={idx} />;
        }
        if (section.section_kind === 'centered_text') {
          return <CenteredTextBlock key={section.id} section={section} />;
        }
        if (section.section_kind === 'image') {
          return <ImageBlock key={section.id} section={section} />;
        }
        return null;
      })}
    </div>
  );
}

export default LandingIntroBlockStack;
